import React, { useEffect, useState } from "react";
import { Menu, X, Phone, Lock } from "lucide-react";
import { PWA } from "@/constants/testIds";
import { BRAND, NAV_LINKS, LOGO_URL } from "./data";

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav
      data-testid={PWA.navBar}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#121212]/92 backdrop-blur-md border-b border-white/10 py-3"
          : "bg-gradient-to-b from-black/55 to-transparent py-5"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex items-center justify-between">
        <button
          data-testid={PWA.navLogo}
          onClick={() => handleClick("#home")}
          className="flex items-center gap-3 group"
          aria-label="Precision Woodworking Artistry"
        >
          <span className="block w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-1 ring-[#C19A6B]/40 bg-[#FAF9F6]">
            <img src={LOGO_URL} alt="Precision Woodworking Artistry logo" className="w-full h-full object-cover" />
          </span>
          <span className="hidden sm:flex flex-col leading-tight text-left">
            <span className="font-serif-display text-[#FAF9F6] text-lg md:text-xl tracking-tight">
              Precision Woodworking
            </span>
            <span className="text-[10px] tracking-[0.32em] text-[#C19A6B] uppercase font-semibold">
              Artistry · Est 2018
            </span>
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`pwa-nav-link-${l.id}`}
                onClick={() => handleClick(l.href)}
                className="link-line text-[12px] tracking-[0.28em] uppercase text-[#FAF9F6]/85 hover:text-[#FAF9F6] transition-colors font-medium"
              >
                {l.label}
              </button>
            </li>
          ))}
          <li>
            <a
              href="/admin"
              data-testid="pwa-nav-admin-link"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] tracking-[0.28em] uppercase text-[#C19A6B] hover:text-[#D9B27F] transition-colors font-medium"
            >
              <Lock className="w-3 h-3 shrink-0" />
              Admin
            </a>
          </li>
        </ul>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${BRAND.phoneTel}`}
            className="flex items-center gap-2 text-[#FAF9F6]/80 hover:text-[#C19A6B] text-sm transition-colors"
            data-testid="pwa-nav-phone"
          >
            <Phone className="w-4 h-4" />
            <span className="tracking-wide">{BRAND.phone}</span>
          </a>
          <button
            data-testid={PWA.navCtaQuote}
            onClick={() => handleClick("#quote")}
            className="btn-gold"
          >
            Get a Quote
          </button>
        </div>

        <button
          data-testid={PWA.navMobileToggle}
          className="lg:hidden text-[#FAF9F6] p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid={PWA.navMobileMenu}
          className="lg:hidden bg-[#121212] border-t border-white/10"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`pwa-nav-link-mobile-${l.id}`}
                onClick={() => handleClick(l.href)}
                className="text-left text-[13px] tracking-[0.28em] uppercase text-[#FAF9F6]/90 hover:text-[#C19A6B]"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${BRAND.phoneTel}`}
              className="flex items-center gap-2 text-[#C19A6B] text-sm pt-2 border-t border-white/10"
            >
              <Phone className="w-4 h-4" />
              {BRAND.phone}
            </a>
            <button
              onClick={() => handleClick("#quote")}
              className="btn-gold w-full justify-center"
              data-testid="pwa-nav-cta-quote-mobile"
            >
              Get a Quote
            </button>
            <a
              href="/admin"
              data-testid="pwa-nav-admin-link-mobile"
              className="mt-2 pt-4 border-t border-white/10 flex items-center gap-3 text-[#FAF9F6]/70 hover:text-[#D9B27F] text-[12px] tracking-[0.28em] uppercase"
            >
              <Lock className="w-3.5 h-3.5" />
              Admin CRM
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
